import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { wrapTablesInScrollContainers } from "../../utils/contentTransforms";
import { exportSharedPdf, loadPdfOptions } from "../../utils/exportUtils";

// Styles — reuse the document viewer's look
import "../../css/DocumentationMainPage.css";
import "../../css/Document.css";
import "../../css/DocumentationHomeComponent.css";
import "../../css/HomePage.css";
import "../../css/DocViewerMakeover.css";
import "../../css/SharedDocumentView.css";

/**
 * Account-less read-only view of a shared document (BBD-59 / BBD-60).
 *
 * Reached from the share link emailed to a recipient: /shared/:token.
 * The token prefix decides the backend:
 *   ps_… → personal documentation share (Permission.share_token)
 *   ws_… → workspace external share (TeamDocumentExternalShare.share_token)
 *
 * No authentication involved — possession of the secret token is the
 * authorization, and revoking the share kills the link.
 */

const API_BASE = "https://bluebirddocumentationadmin.pythonanywhere.com";

function SharedDocumentView() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [doc, setDoc] = useState(null);
    // "Download PDF" (BBD-119) — works without an account, like the link itself
    const [pdfBusy, setPdfBusy] = useState(false);
    const [pdfError, setPdfError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const fetchSharedDocument = async () => {
            setLoading(true);
            setError(null);
            try {
                let endpoint;
                if (token && token.startsWith("ws_")) {
                    endpoint = `${API_BASE}/workspaces/shared/${token}/`;
                } else if (token && token.startsWith("ps_")) {
                    endpoint = `${API_BASE}/documentation/shared/${token}/`;
                } else {
                    if (!cancelled) {
                        setError("This share link is invalid or has been revoked.");
                        setLoading(false);
                    }
                    return;
                }

                const response = await fetch(endpoint);
                const data = await response.json();

                if (!response.ok) {
                    if (!cancelled) {
                        setError(data.error || "This share link is invalid or has been revoked.");
                        setLoading(false);
                    }
                    return;
                }

                if (!cancelled) {
                    setDoc({ ...data, content: transformContent(data.content) });
                    setLoading(false);
                }
            } catch (err) {
                console.error("Error fetching shared document:", err);
                if (!cancelled) {
                    setError("Something went wrong loading this document. Please try again.");
                    setLoading(false);
                }
            }
        };

        fetchSharedDocument();
        return () => { cancelled = true; };
    }, [token]);

    // Same display transforms as the normal document viewer
    const transformContent = (htmlContent) => {
        if (!htmlContent) return "";
        const parser = new DOMParser();
        const parsed = parser.parseFromString(htmlContent, "text/html");

        // Demote headings two levels for display (h1→h3 … h4→h6), deepest first
        // so freshly created tags aren't re-demoted.
        [["h4", "h6"], ["h3", "h5"], ["h2", "h4"], ["h1", "h3"]].forEach(([from, to]) => {
            parsed.querySelectorAll(from).forEach((heading) => {
                const demoted = document.createElement(to);
                demoted.innerHTML = heading.innerHTML;
                heading.replaceWith(demoted);
            });
        });

        wrapTablesInScrollContainers(parsed);
        return parsed.body.innerHTML;
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "";
        return new Date(timestamp).toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const handleDownloadPdf = async () => {
        if (pdfBusy) return;
        setPdfBusy(true);
        setPdfError(null);
        try {
            await exportSharedPdf({ shareToken: token, title: doc.name, options: loadPdfOptions() });
        } catch (err) {
            console.error("Error downloading shared document as PDF:", err);
            setPdfError(err.message);
        } finally {
            setPdfBusy(false);
        }
    };

    const handleAttachmentDownload = (attachment) => {
        const link = document.createElement("a");
        link.href = `${API_BASE}/attachments/${attachment.id}/download/`;
        link.download = attachment.file_name;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <div className="loading-animation-div" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh" }}>
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: "#5B92E5", fontSize: "1.1rem", marginTop: 16 }}>Opening shared document...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="DocumentationMainPage__div DocViewer SharedDoc">
                <div className="DocumentationMainPage__main-content">
                    <div className="SharedDoc__error-card">
                        <i className="fas fa-link-slash SharedDoc__error-icon"></i>
                        <p className="large">We couldn't open this shared document.</p>
                        <p className="medium">{error}</p>
                        <p className="medium">
                            If someone shared a document with you, make sure you're using the
                            exact link from the email. The link stops working if the share is
                            removed by the document's owner.
                        </p>
                        <div className="SharedDoc__error-actions">
                            <Link to="/" className="ConflictResolver__button">Home</Link>
                            <Link to="/login" className="ConflictResolver__button">Login</Link>
                            <Link to="/register" className="ConflictResolver__button">Register</Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="DocumentationMainPage__div DocViewer SharedDoc">
            <div className="DocumentationMainPage__main-content">

                <div className="SharedDoc__banner">
                    <i className="fas fa-envelope-open-text"></i>
                    <span>
                        Shared with <strong>{doc.shared_with}</strong> · view only
                        {doc.workspace_name && (
                            <> · from the <strong>{doc.workspace_name}</strong> workspace</>
                        )}
                    </span>
                    <div className="SharedDoc__banner-actions">
                        <button type="button" className="SharedDoc__banner-button" onClick={handleDownloadPdf} disabled={pdfBusy}>
                            <i className="fa-solid fa-file-pdf"></i>
                            {pdfBusy ? "Preparing PDF…" : "Download PDF"}
                        </button>
                        <button type="button" className="SharedDoc__banner-button" onClick={() => window.print()}>
                            <i className="fa-solid fa-print"></i>
                            Print
                        </button>
                        <Link to="/register" className="SharedDoc__banner-cta">
                            Create a free account
                        </Link>
                    </div>
                    {pdfError && <p className="SharedDoc__banner-error" role="alert">{pdfError}</p>}
                </div>

                <div className="Document__div">
                    <h2 className="Document__div-title">{doc.name}</h2>
                    {/* Shown on paper only (Ctrl+P, BBD-120) */}
                    {doc.description && doc.description !== "null" && (
                        <p className="Document__print-description">{doc.description}</p>
                    )}

                    {doc.attachments && doc.attachments.length > 0 && (
                        <div className="Document__attachments-section">
                            <div className="Document__attachments-list">
                                {doc.attachments.map((attachment, index) => (
                                    <div key={attachment.id || index} className="Document__attachment-item">
                                        <div className="Document__attachment-info">
                                            <div className="Document__attachment-name-row">
                                                <i className="fas fa-file-archive Document__attachment-icon"></i>
                                                <span className="Document__attachment-name">{attachment.file_name}</span>
                                            </div>
                                            <div className="Document__attachment-details">
                                                <span className="Document__attachment-detail-item">
                                                    <i className="fas fa-weight-hanging"></i>
                                                    {formatFileSize(attachment.file_size)}
                                                </span>
                                                <span className="Document__attachment-detail-item">
                                                    <i className="fas fa-calendar"></i>
                                                    {formatTimestamp(attachment.created_at)}
                                                </span>
                                            </div>
                                            {attachment.description && (
                                                <div className="Document__attachment-description">{attachment.description}</div>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleAttachmentDownload(attachment)}
                                            className="Document__attachment-download-btn"
                                            title={`Download ${attachment.file_name}`}
                                        >
                                            <i className="fas fa-download"></i>
                                            Download
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {doc.author_name && (
                        <p className="extra-small" style={{ paddingTop: "10px" }}>
                            <span className="semi-bold">Author:</span> <i>{doc.author_name}</i>
                        </p>
                    )}
                    <p className="extra-small" style={{ paddingTop: "10px" }}>
                        <span className="semi-bold">Created At:</span> <i>{formatTimestamp(doc.created_at)}</i>
                    </p>
                    <p className="extra-small" style={{ paddingBottom: "30px" }}>
                        <span className="semi-bold">Last Updated At:</span> <i>{formatTimestamp(doc.updated_at)}</i>
                    </p>

                    <div
                        className="ql-editor ql-editor-styling"
                        dangerouslySetInnerHTML={{ __html: doc.content }}
                    />
                </div>

                <p className="SharedDoc__footer-note">
                    Viewing a document shared through{" "}
                    <a href="https://bluebird-documentation.com">Bluebird Documentation</a>.
                    {doc.can_edit && (
                        <> You have edit access — <Link to="/login">log in</Link> or{" "}
                        <Link to="/register">create an account</Link> to edit this document.</>
                    )}
                </p>

            </div>
        </div>
    );
}

export default SharedDocumentView;
